import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MapPin, Trash2, Users, Loader2, Radio, LocateFixed, Smile, ImagePlus, X, Map as MapIcon } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { UserProfileCard } from "@/components/UserProfileCard";

const initials = (n) => (n || "U").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

function AutoTextarea({ value, onChange, onSend, placeholder, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 140) + "px"; }
  }, [value]);
  return (
    <textarea ref={ref} value={value} onChange={onChange} rows={1} placeholder={placeholder}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
      maxLength={1000} data-testid={testid}
      className="flex-1 resize-none rounded-xl border border-input bg-background px-3 py-2 text-sm leading-relaxed outline-none transition-[height] focus-visible:ring-2 focus-visible:ring-ring max-h-[140px] no-scrollbar" />
  );
}

const EMOJIS = ["💪", "🔥", "🏋️", "🏃", "🚴", "🥗", "😀", "😁", "😎", "🤩", "👍", "👏", "🙌", "🎯", "✅", "💯", "❤️", "⚡", "🏆", "🥇", "😅", "😤", "🤝", "🙏", "😴", "💦", "🍗", "🥤", "🧘", "⏱️", "📈", "🎉"];
const QUICK_REACTIONS = ["👍", "🔥", "❤️"];

async function resizeImage(file, maxW = 1080) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Chat() {
  const { t } = useI18n();
  const [tab, setTab] = useState("community");
  const [dmUser, setDmUser] = useState(null);
  const [dmUnread, setDmUnread] = useState(0);
  const [cardId, setCardId] = useState(null);

  const openDm = useCallback((u) => {
    if (!u?.user_id) return;
    setDmUser({ user_id: u.user_id, name: u.name, picture: u.picture });
    setTab("dm");
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.state?.openDm) {
      openDm(location.state.openDm);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, openDm, navigate]);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try { const { data } = await api.get("/dm/unread"); if (alive) setDmUnread(data.count || 0); } catch { /* noop */ }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [tab]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 rounded-full border bg-card p-1 w-fit" data-testid="chat-tabs">
        <button onClick={() => setTab("community")} data-testid="tab-community"
          className={cn("rounded-full px-4 py-1.5 text-sm font-semibold transition-colors", tab === "community" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
          {t("communityTab")}
        </button>
        <button onClick={() => setTab("dm")} data-testid="tab-dm"
          className={cn("relative rounded-full px-4 py-1.5 text-sm font-semibold transition-colors", tab === "dm" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
          {t("messagesTab")}
          {dmUnread > 0 && <span className="ml-1.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white tabular-nums" data-testid="dm-unread-badge">{dmUnread}</span>}
        </button>
      </div>
      {tab === "community"
        ? <CommunityChat onOpenDm={openDm} onOpenProfile={setCardId} />
        : <DirectMessages initialUser={dmUser} onUnreadChange={setDmUnread} />}

      <UserProfileCard userId={cardId} open={!!cardId} onClose={() => setCardId(null)} />
    </div>
  );
}

function CommunityChat({ onOpenDm, onOpenProfile }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [text, setText] = useState("");
  const [gym, setGym] = useState("");
  const [gymInput, setGymInput] = useState("");
  const [editingGym, setEditingGym] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reactions, setReactions] = useState({});
  const fileRef = useRef(null);

  const cursorRef = useRef(null);
  const pendingGymRef = useRef(undefined); // undefined = don't send; string|null = write
  const pendingCoordsRef = useRef(undefined); // undefined = don't send; {lat,lng}|null = write
  const gymInitRef = useRef(false);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);

  const sync = useCallback(async () => {
    const body = { after: cursorRef.current };
    if (pendingGymRef.current !== undefined) body.gym_location = pendingGymRef.current;
    if (pendingCoordsRef.current !== undefined) {
      body.gym_lat = pendingCoordsRef.current ? pendingCoordsRef.current.lat : null;
      body.gym_lng = pendingCoordsRef.current ? pendingCoordsRef.current.lng : null;
    }
    try {
      const { data } = await api.post("/chat/sync", body);
      if (pendingGymRef.current !== undefined) pendingGymRef.current = undefined;
      if (pendingCoordsRef.current !== undefined) pendingCoordsRef.current = undefined;
      cursorRef.current = data.server_time;
      setOnline(data.online || []);
      if (data.reactions) setReactions(data.reactions);
      try { localStorage.setItem("fp_chat_read_at", data.server_time); } catch { /* noop */ }
      window.dispatchEvent(new Event("fp-chat-read"));
      if (!gymInitRef.current) {
        gymInitRef.current = true;
        if (data.my_gym_location) { setGym(data.my_gym_location); setGymInput(data.my_gym_location); }
      }
      const delSet = new Set(data.deleted_ids || []);
      setMessages((prev) => {
        let next = prev.filter((m) => !delSet.has(m.id));
        if (data.messages?.length) {
          const ids = new Set(next.map((m) => m.id));
          next = [...next, ...data.messages.filter((m) => !ids.has(m.id))];
        }
        return next;
      });
      setLoaded(true);
    } catch { /* transient */ }
  }, []);

  useEffect(() => {
    sync();
    const id = setInterval(sync, 3000);
    return () => clearInterval(id);
  }, [sync]);

  // autoscroll when messages grow
  const lastCountRef = useRef(0);
  useEffect(() => {
    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length;
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const send = async () => {
    const val = text.trim();
    if ((!val && !pendingImage) || sending) return;
    setSending(true);
    const img = pendingImage;
    setText(""); setPendingImage(null);
    try {
      const { data } = await api.post("/chat/messages", { text: val, image: img || undefined });
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); setText(val); setPendingImage(img); } finally { setSending(false); }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) { toast.error(t("photoInvalidType")); return; }
    if (file.size > 8_000_000) { toast.error(t("photoTooLarge")); return; }
    try { setPendingImage(await resizeImage(file)); } catch { toast.error("Error"); }
  };

  const addEmoji = (e) => { setText((prev) => prev + e); setEmojiOpen(false); };

  const react = async (id, emoji) => {
    const uid = user?.user_id;
    setReactions((prev) => {
      const cur = { ...(prev[id] || {}) };
      const users = new Set(cur[emoji] || []);
      if (users.has(uid)) users.delete(uid); else users.add(uid);
      if (users.size) cur[emoji] = [...users]; else delete cur[emoji];
      return { ...prev, [id]: cur };
    });
    try {
      const { data } = await api.post(`/chat/messages/${id}/react`, { emoji });
      setReactions((prev) => ({ ...prev, [id]: data.reactions || {} }));
    } catch { /* revert on next sync */ }
  };

  const remove = async (id) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    try { await api.delete(`/chat/messages/${id}`); } catch { toast.error("Error"); }
  };

  const commitGym = (value) => {
    const v = (value || "").trim() || null;
    setGym(v || "");
    setGymInput(v || "");
    pendingGymRef.current = v;
    setEditingGym(false);
    sync();
  };

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error(t("geoUnsupported")); return; }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`, { headers: { "Accept-Language": "id" } });
        const d = await r.json();
        const a = d.address || {};
        const area = a.suburb || a.neighbourhood || a.village || a.town || a.city_district;
        const city = a.city || a.county || a.state;
        const label = [area, city].filter(Boolean).join(", ") || d.display_name || `${latitude.toFixed(3)}, ${longitude.toFixed(3)}`;
        pendingCoordsRef.current = { lat: latitude, lng: longitude };
        commitGym(label);
        toast.success(t("locationDetected"));
      } catch { toast.error(t("geoFailed")); } finally { setDetecting(false); }
    }, () => { setDetecting(false); toast.error(t("geoDenied")); }, { timeout: 10000, enableHighAccuracy: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("chat")}</h1>
          <p className="text-sm text-muted-foreground">{t("chatSubtitle")}</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm" data-testid="online-count">
          <Radio className="h-4 w-4 text-emerald-500" />
          <span className="font-semibold text-emerald-500">{online.length}</span>
          <span className="text-muted-foreground">{t("online")}</span>
        </div>
      </div>

      {/* Gym location bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-card p-3" data-testid="gym-location-bar">
        <MapPin className="h-4 w-4 shrink-0 text-primary" />
        {editingGym ? (
          <>
            <Input value={gymInput} onChange={(e) => setGymInput(e.target.value)} placeholder={t("gymPlaceholder")}
              className="h-8 flex-1 min-w-[160px]" data-testid="gym-input" onKeyDown={(e) => e.key === "Enter" && commitGym(gymInput)} />
            <Button size="sm" onClick={() => commitGym(gymInput)} data-testid="gym-save-btn">{t("save")}</Button>
          </>
        ) : (
          <>
            <span className="flex-1 text-sm" data-testid="gym-display">
              {gym ? <span className="font-medium">{gym}</span> : <span className="text-muted-foreground">{t("noGymSet")}</span>}
            </span>
            <Button size="sm" variant="outline" onClick={() => setEditingGym(true)} data-testid="gym-edit-btn">{gym ? t("change") : t("setGym")}</Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={detectLocation} disabled={detecting} className="gap-1.5" data-testid="detect-location-btn">
          {detecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} {t("detectLocation")}
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_260px] [&>*]:min-w-0">
        {/* Messages */}
        <div className="flex h-[62vh] flex-col rounded-2xl border bg-card">
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4 no-scrollbar" data-testid="chat-messages">
            {!loaded ? (
              <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
                <Users className="mb-2 h-10 w-10 opacity-40" />
                <p className="text-sm">{t("chatEmpty")}</p>
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.user_id === user?.user_id;
                const canDelete = mine || user?.role === "admin";
                const msgReacts = reactions[m.id] || {};
                const reactChips = Object.entries(msgReacts).filter(([, u]) => u?.length);
                return (
                  <div key={m.id} tabIndex={-1} className={cn("group flex items-end gap-2 outline-none", mine ? "flex-row-reverse" : "")} data-testid={`msg-${m.id}`}>
                    {!mine && (
                      <button type="button" onClick={() => onOpenDm?.({ user_id: m.user_id, name: m.name, picture: m.picture })}
                        data-testid={`dm-open-${m.user_id}`} title={t("sendMessage")}>
                        <Avatar className="h-8 w-8 shrink-0 cursor-pointer transition-transform hover:scale-110"><AvatarImage src={m.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-[11px] font-semibold text-primary">{initials(m.name)}</AvatarFallback></Avatar>
                      </button>
                    )}
                    <div className={cn("flex max-w-[80%] flex-col", mine ? "items-end" : "items-start")}>
                      <div className={cn("rounded-2xl px-3.5 py-2", mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-accent rounded-bl-sm")}>
                        {!mine && <button type="button" onClick={() => onOpenProfile?.(m.user_id)} className="mb-0.5 text-xs font-semibold text-primary hover:underline" data-testid={`chat-profile-${m.user_id}`}>{m.name}</button>}
                        {m.image && (
                          <button type="button" onClick={() => setPreview(m.image)} className="mb-1 block">
                            <img src={m.image} alt="" className="max-h-56 rounded-xl object-cover" data-testid={`msg-image-${m.id}`} />
                          </button>
                        )}
                        {m.text && <div className="whitespace-pre-wrap break-words text-sm">{m.text}</div>}
                        <div className={cn("mt-1 text-[10px]", mine ? "text-primary-foreground/70" : "text-muted-foreground")}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      {reactChips.length > 0 && (
                        <div className={cn("mt-1 flex flex-wrap gap-1", mine ? "justify-end" : "")}>
                          {reactChips.map(([emoji, users]) => {
                            const reacted = users.includes(user?.user_id);
                            return (
                              <button key={emoji} onClick={() => react(m.id, emoji)} data-testid={`reaction-${m.id}-${emoji}`}
                                className={cn("flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors",
                                  reacted ? "border-primary/50 bg-primary/10 text-primary" : "bg-card hover:bg-accent")}>
                                <span>{emoji}</span><span className="tabular-nums">{users.length}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className={cn("flex items-center gap-0.5 self-center opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100", mine ? "flex-row-reverse" : "")}>
                      {QUICK_REACTIONS.map((e) => (
                        <button key={e} onClick={() => react(m.id, e)} data-testid={`quick-react-${m.id}-${e}`}
                          className="grid h-8 w-8 place-items-center rounded-full text-sm hover:bg-accent">{e}</button>
                      ))}
                      {canDelete && (
                        <button onClick={() => remove(m.id)} data-testid={`msg-delete-${m.id}`} aria-label="Delete"
                          className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
          {pendingImage && (
            <div className="flex items-center gap-3 border-t px-3 pt-3" data-testid="image-preview">
              <img src={pendingImage} alt="" className="h-16 w-16 rounded-lg object-cover" />
              <span className="flex-1 text-xs text-muted-foreground">{t("imageReady")}</span>
              <button onClick={() => setPendingImage(null)} className="text-muted-foreground hover:text-destructive" data-testid="remove-image-btn"><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="flex items-end gap-2 border-t p-3">
            <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={onPickImage} data-testid="chat-image-input" />
            <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} className="shrink-0 text-muted-foreground" data-testid="chat-image-btn"><ImagePlus className="h-5 w-5" /></Button>
            <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0 text-muted-foreground" data-testid="chat-emoji-btn"><Smile className="h-5 w-5" /></Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="start">
                <div className="grid grid-cols-8 gap-1" data-testid="emoji-grid">
                  {EMOJIS.map((e) => (
                    <button key={e} onClick={() => addEmoji(e)} className="rounded-md p-1.5 text-lg transition-colors hover:bg-accent" data-testid={`emoji-${e}`}>{e}</button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <AutoTextarea value={text} onChange={(e) => setText(e.target.value)} onSend={send} placeholder={t("typeMessage")} testid="chat-input" />
            <Button onClick={send} disabled={sending || (!text.trim() && !pendingImage)} className="shrink-0 gap-1.5" data-testid="chat-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Online users */}
        <div className="space-y-4">
          <GymMap users={online} meId={user?.user_id} t={t} />
          <div className="rounded-2xl border bg-card p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Radio className="h-4 w-4 text-emerald-500" /> {t("whoIsOnline")}</h3>
            <div className="max-h-[42vh] space-y-2 overflow-y-auto no-scrollbar" data-testid="online-list">
              {online.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t("noOneOnline")}</p>
              ) : online.map((u) => {
                const isMe = u.user_id === user?.user_id;
                return (
                <button key={u.user_id} type="button" disabled={isMe}
                  onClick={() => !isMe && onOpenDm?.({ user_id: u.user_id, name: u.name, picture: u.picture })}
                  className={cn("flex w-full items-center gap-2.5 rounded-xl p-1.5 text-left transition-colors", !isMe && "hover:bg-accent")}
                  data-testid={`online-${u.user_id}`}>
                  <div className="relative shrink-0">
                    <Avatar className="h-9 w-9"><AvatarImage src={u.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-[11px] font-semibold text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{u.name}{isMe ? ` (${t("you")})` : ""}</div>
                    <div className="flex items-center gap-1 truncate text-[11px] text-muted-foreground">
                      {u.gym_location ? <><MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{u.gym_location}</span></> : <span className="italic opacity-70">{t("locationHidden")}</span>}
                    </div>
                  </div>
                  {!isMe && <Send className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                </button>
              );})}
            </div>
          </div>
        </div>
      </div>

      {preview && (
        <div onClick={() => setPreview(null)} data-testid="image-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <img src={preview} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain" />
          <button className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white" data-testid="lightbox-close"><X className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}


function DirectMessages({ initialUser, onUnreadChange }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(initialUser || null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const cursorRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try { const { data } = await api.get("/dm/conversations"); setConvs(data || []); } catch { /* noop */ }
  }, []);

  useEffect(() => { loadConvs(); const id = setInterval(loadConvs, 5000); return () => clearInterval(id); }, [loadConvs]);
  useEffect(() => { if (initialUser) setActive(initialUser); }, [initialUser]);

  const syncThread = useCallback(async (reset) => {
    if (!active?.user_id) return;
    try {
      const { data } = await api.get(`/dm/${active.user_id}`, { params: reset ? {} : { after: cursorRef.current } });
      cursorRef.current = data.server_time;
      setActive((a) => ({ ...a, name: data.other?.name || a?.name, picture: data.other?.picture ?? a?.picture, online: data.other?.online }));
      setMessages((prev) => {
        if (reset) return data.messages || [];
        if (!data.messages?.length) return prev;
        const ids = new Set(prev.map((m) => m.id));
        return [...prev, ...data.messages.filter((m) => !ids.has(m.id))];
      });
      setLoaded(true);
      try { const { data: un } = await api.get("/dm/unread"); onUnreadChange?.(un.count || 0); window.dispatchEvent(new Event("fp-dm-read")); } catch { /* noop */ }
    } catch { /* noop */ }
  }, [active?.user_id, onUnreadChange]);

  useEffect(() => {
    if (!active?.user_id) return;
    cursorRef.current = null; setMessages([]); setLoaded(false);
    syncThread(true);
    const id = setInterval(() => syncThread(false), 3000);
    return () => clearInterval(id);
  }, [active?.user_id, syncThread]);

  const lastCountRef = useRef(0);
  useEffect(() => {
    if (messages.length !== lastCountRef.current) { lastCountRef.current = messages.length; bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }
  }, [messages]);

  const send = async () => {
    const val = text.trim();
    if ((!val && !pendingImage) || sending || !active?.user_id) return;
    setSending(true);
    const img = pendingImage;
    setText(""); setPendingImage(null);
    try {
      const { data } = await api.post(`/dm/${active.user_id}`, { text: val, image: img || undefined });
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      loadConvs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); setText(val); setPendingImage(img); } finally { setSending(false); }
  };

  const deleteDm = async (id) => {
    const prev = messages;
    setMessages((ms) => ms.map((m) => m.id === id ? { ...m, deleted: true, text: "", image: null } : m));
    try { await api.delete(`/dm/message/${id}`); loadConvs(); }
    catch (e) { setMessages(prev); toast.error(e?.response?.data?.detail || "Error"); }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) { toast.error(t("photoInvalidType")); return; }
    if (file.size > 8_000_000) { toast.error(t("photoTooLarge")); return; }
    try { setPendingImage(await resizeImage(file)); } catch { toast.error("Error"); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr] [&>*]:min-w-0">
      {/* Conversation list */}
      <div className={cn("rounded-2xl border bg-card", active && "hidden lg:block")}>
        <div className="border-b px-4 py-3 text-sm font-semibold">{t("messagesTab")}</div>
        <div className="max-h-[60vh] overflow-y-auto no-scrollbar p-2" data-testid="dm-conv-list">
          {convs.length === 0 ? (
            <p className="p-4 text-center text-xs text-muted-foreground">{t("noConversations")}</p>
          ) : convs.map((c) => (
            <button key={c.conv_id} onClick={() => setActive({ user_id: c.other_id, name: c.name, picture: c.picture })}
              data-testid={`dm-conv-${c.other_id}`}
              className={cn("flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-accent",
                active?.user_id === c.other_id && "bg-accent")}>
              <div className="relative shrink-0">
                <Avatar className="h-10 w-10"><AvatarImage src={c.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">{initials(c.name)}</AvatarFallback></Avatar>
                {c.online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold">{c.name}</span>
                  {c.unread > 0 && <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white tabular-nums">{c.unread}</span>}
                </div>
                <div className="truncate text-xs text-muted-foreground">{c.last_from_me ? `${t("you")}: ` : ""}{c.last_text}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className={cn("flex h-[62vh] flex-col rounded-2xl border bg-card", !active && "hidden lg:flex")}>
        {!active ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
            <Send className="mb-2 h-10 w-10 opacity-40" />
            <p className="text-sm">{t("selectConversation")}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 border-b p-3">
              <button className="lg:hidden text-muted-foreground" onClick={() => setActive(null)} data-testid="dm-back-btn"><X className="h-5 w-5" /></button>
              <div className="relative shrink-0">
                <Avatar className="h-9 w-9"><AvatarImage src={active.picture} className="object-cover" /><AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">{initials(active.name)}</AvatarFallback></Avatar>
                {active.online && <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />}
              </div>
              <div>
                <div className="text-sm font-semibold" data-testid="dm-thread-name">{active.name}</div>
                <div className="text-[11px] text-muted-foreground">{active.online ? t("online") : t("offline")}</div>
              </div>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-4 no-scrollbar" data-testid="dm-thread">
              {!loaded ? (
                <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
                  <p className="text-sm">{t("dmEmpty")}</p>
                </div>
              ) : messages.map((m) => {
                const mine = m.from_id === user?.user_id;
                return (
                  <div key={m.id} tabIndex={-1} className={cn("group flex items-center gap-1 outline-none", mine ? "justify-end" : "justify-start")} data-testid={`dm-msg-${m.id}`}>
                    {mine && !m.deleted && (
                      <button onClick={() => deleteDm(m.id)} data-testid={`dm-delete-${m.id}`} title={t("unsend")}
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-focus-within:opacity-100 group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <div className={cn("max-w-[75%] rounded-2xl px-3.5 py-2",
                      m.deleted ? "border border-dashed bg-transparent text-muted-foreground" : mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-accent rounded-bl-sm")}>
                      {m.deleted ? (
                        <div className="text-sm italic">{t("messageDeleted")}</div>
                      ) : (
                        <>
                          {m.image && (
                            <button type="button" onClick={() => setPreview(m.image)} className="mb-1 block">
                              <img src={m.image} alt="" className="max-h-56 rounded-xl object-cover" data-testid={`dm-image-${m.id}`} />
                            </button>
                          )}
                          {m.text && <div className="whitespace-pre-wrap break-words text-sm">{m.text}</div>}
                          <div className={cn("mt-1 text-[10px]", mine ? "text-primary-foreground/70" : "text-muted-foreground")}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {mine && m.read && <span className="ml-1">· {t("readReceipt")}</span>}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            {pendingImage && (
              <div className="flex items-center gap-3 border-t px-3 pt-3" data-testid="dm-image-preview">
                <img src={pendingImage} alt="" className="h-16 w-16 rounded-lg object-cover" />
                <span className="flex-1 text-xs text-muted-foreground">{t("imageReady")}</span>
                <button onClick={() => setPendingImage(null)} className="text-muted-foreground hover:text-destructive" data-testid="dm-remove-image-btn"><X className="h-4 w-4" /></button>
              </div>
            )}
            <div className="flex items-end gap-2 border-t p-3">
              <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" className="hidden" onChange={onPickImage} data-testid="dm-image-input" />
              <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} className="shrink-0 text-muted-foreground" data-testid="dm-image-btn"><ImagePlus className="h-5 w-5" /></Button>
              <AutoTextarea value={text} onChange={(e) => setText(e.target.value)} onSend={send} placeholder={t("typeMessage")} testid="dm-input" />
              <Button onClick={send} disabled={sending || (!text.trim() && !pendingImage)} className="shrink-0 gap-1.5" data-testid="dm-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </>
        )}
      </div>

      {preview && (
        <div onClick={() => setPreview(null)} data-testid="dm-image-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <img src={preview} alt="" className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain" />
          <button className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white" data-testid="dm-lightbox-close"><X className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}

function GymMap({ users, meId, t }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const withCoords = (users || []).filter((u) => typeof u.gym_lat === "number" && typeof u.gym_lng === "number");
  const sig = JSON.stringify(withCoords.map((u) => [u.user_id, u.gym_lat, u.gym_lng, u.name]));

  useEffect(() => {
    if (!elRef.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(elRef.current, { zoomControl: true, attributionControl: false }).setView([-2.5, 118], 4);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }
    const map = mapRef.current;
    const layer = layerRef.current;
    layer.clearLayers();
    const pts = [];
    withCoords.forEach((u) => {
      const mine = u.user_id === meId;
      const color = mine ? "#10b981" : "#6366f1";
      const icon = L.divIcon({
        className: "",
        html: `<div style="position:relative"><div style="width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div></div>`,
        iconSize: [20, 20], iconAnchor: [10, 20], popupAnchor: [0, -18],
      });
      const mk = L.marker([u.gym_lat, u.gym_lng], { icon }).addTo(layer);
      mk.bindPopup(`<b>${(u.name || "").replace(/</g, "")}${mine ? " (" + t("you") + ")" : ""}</b>${u.gym_location ? "<br/>" + String(u.gym_location).replace(/</g, "") : ""}`);
      pts.push([u.gym_lat, u.gym_lng]);
    });
    const tid = setTimeout(() => {
      if (!mapRef.current) return;
      map.invalidateSize();
      if (pts.length === 1) map.setView(pts[0], 13);
      else if (pts.length > 1) map.fitBounds(pts, { padding: [30, 30], maxZoom: 13 });
    }, 60);
    return () => clearTimeout(tid);
  }, [sig, meId, t, withCoords]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  return (
    <div className="overflow-hidden rounded-2xl border bg-card" data-testid="gym-map-card">
      <div className="flex items-center gap-2 border-b px-4 py-2.5 text-sm font-semibold"><MapIcon className="h-4 w-4 text-primary" /> {t("gymMap")}</div>
      <div className="relative">
        <div ref={elRef} className="h-48 w-full" data-testid="gym-map" style={{ zIndex: 0 }} />
        {withCoords.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-card/70 px-4 text-center text-xs text-muted-foreground">
            {t("noMapPins")}
          </div>
        )}
      </div>
    </div>
  );
}
